import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const AuthContext = createContext();

const getApiUrl = () => {
  // In productie: altijd zelfde origin + /api, nooit localhost
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
    const url = `${window.location.origin}/api`;
    console.log('Using same-origin API URL (production):', url);
    return url;
  }

  // In development: optioneel override via REACT_APP_API_URL
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    const url = envUrl.replace(/\/$/, '');
    console.log('Using API URL from REACT_APP_API_URL (dev):', url);
    return url;
  }

  // Development default: backend op poort 5001
  const url = 'http://localhost:5001/api';
  console.log('Using default API URL (dev localhost):', url);
  return url;
};

const API_URL = getApiUrl();
console.log('API_URL configured as:', API_URL);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Set up axios defaults
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      // Prevent an indefinitely "blank" app if the API never responds (cold start / misconfig).
      const response = await axios.get(`${API_URL}/auth/me`, { timeout: 15000 });
      setCurrentUser(response.data);
    } catch (err) {
      console.error('Failed to fetch user data', err);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (usernameOrEmail, password) => {
    try {
      setError('');
      const loginUrl = `${API_URL}/auth/login`;
      console.log('Attempting login to:', loginUrl);

      // Send as username (backend accepts either username or email); timeout for cold start / slow network
      const response = await axios.post(loginUrl, {
        username: usernameOrEmail,
        password
      }, { timeout: 20000 });
      const { token, refreshToken, ...user } = response.data;

      localStorage.setItem('token', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setCurrentUser(user);

      // Removed navigation from here to let components handle it
      return { success: true };
    } catch (err) {
      console.error('Login error:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: `${API_URL}/auth/login`
      });

      const message = err.response?.data?.message || err.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      setError('');
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      const { token, refreshToken, ...user } = response.data;

      localStorage.setItem('token', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setCurrentUser(user);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    navigate('/login');
  };

  const updateUser = (userData) => {
    setCurrentUser(prev => ({
      ...prev,
      ...userData
    }));
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError('');
      const response = await axios.put(`${API_URL}/auth/change-password`, {
        currentPassword,
        newPassword
      });
      return { success: true, message: response.data.message };
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to change password';
      setError(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user: currentUser,
    loading,
    error,
    login,
    register,
    logout,
    updateUser,
    changePassword,
    isAuthenticated: !!currentUser,
    isAdmin: currentUser?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="loading" style={{ padding: '24px' }}>
          {t('common.loading')}
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export default AuthContext;
